// v49.3 — Trade enum ↔ display label helpers.
// Mirrors v48.281's tradeMap and v48.298's reverse mapping. Centralised
// here so every component (PostForm, Feed, Proposals, Modal) speaks the
// same language about trade strings.

import type { TradeKind, DealStage } from "@/lib/api/types";

export const TRADE_OPTIONS: { id: TradeKind; l: string }[] = [
  { id: "electrician",   l: "Electrician" },
  { id: "cable_jointer", l: "Cable Jointer" },
  { id: "cx_engineer",   l: "Commissioning Engineer" },
  { id: "bms_engineer",  l: "BMS Engineer" },
  { id: "hvac_tech",     l: "HVAC Technician" },
  { id: "mech_fitter",   l: "Mechanical Fitter" },
  { id: "pipefitter",    l: "Pipefitter" },
  { id: "supervisor",    l: "Supervisor" },
  { id: "other",         l: "Other" },
];

const _TRADE_LABEL_MAP: Record<string, string> = Object.fromEntries(
  TRADE_OPTIONS.map(t => [t.id, t.l])
);

export function tradeLabel(t: string | null | undefined): string {
  if (!t) return "Operative";
  return _TRADE_LABEL_MAP[t] || (t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, " "));
}

/* v49.14 — Rightmove-style cover-band palette per trade. Deterministic
   hash so the same trade always gets the same colour, like v48's
   _palettedTrade. Used by RequirementTile cover bands + similar
   future tile components (operative cards, supplier cards). */
const _PALETTES = [
  { bg: "var(--color-lc-blue-light)", to: "#DBEAFE", border: "var(--color-lc-blue-border)", tone: "var(--color-lc-blue-dark)" }, /* blue   */
  { bg: "var(--color-lc-green-bg)", to: "#D1FAE5", border: "#BBF7D0", tone: "var(--color-lc-green)" }, /* green  */
  { bg: "#FEF3F2", to: "#FECACA", border: "#FECACA", tone: "#B91C1C" }, /* red    */
  { bg: "var(--color-lc-amber-bg)", to: "#FEF3C7", border: "#FDE68A", tone: "#92400E" }, /* amber  */
  { bg: "#F5F3FF", to: "#EDE9FE", border: "#DDD6FE", tone: "#6D28D9" }, /* purple */
] as const;

export interface TradePalette {
  bg:     string;
  to:     string;
  border: string;
  tone:   string;
}

export function tradePalette(t: string | null | undefined): TradePalette {
  const s = String(t || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h += s.charCodeAt(i);
  return _PALETTES[h % _PALETTES.length];
}

/* Urgency tone helper for the top-right cover-band chip. */
export function urgencyTone(weeksAway: number | null | undefined): { tone: string; bg: string; border: string; label: string } {
  if (weeksAway == null) return { tone: "var(--color-lc-blue-dark)", bg: "var(--color-lc-blue-light)", border: "var(--color-lc-blue-border)", label: "Open" };
  if (weeksAway <= 1)    return { tone: "var(--color-lc-red)", bg: "#FEF2F2", border: "#FECACA", label: "Imminent" };
  if (weeksAway <= 4)    return { tone: "var(--color-lc-amber)", bg: "var(--color-lc-amber-bg)", border: "#FDE68A", label: `${weeksAway}w` };
  return                       { tone: "var(--color-lc-blue-dark)", bg: "var(--color-lc-blue-light)", border: "var(--color-lc-blue-border)", label: `${weeksAway}w out` };
}

/* Compute weeks-away from a start_date string (YYYY-MM-DD). */
export function weeksAwayFrom(startDate: string | null | undefined): number | null {
  if (!startDate) return null;
  try {
    const days = (new Date(startDate).getTime() - Date.now()) / 86400000;
    if (Number.isNaN(days)) return null;
    return Math.max(0, Math.round(days / 7));
  } catch { return null; }
}

/* v49.16 — fee tier band labels. Mirrors the brief's revenue ladder
   from migration-016. headcount_tier on fee_events is the raw
   headcount, not a tier index. */
export function feeTierBand(headcount: number): { label: string; pct: number } {
  if (headcount >= 100) return { label: "Framework",     pct: 1  };
  if (headcount >= 50)  return { label: "Enterprise",    pct: 2  };
  if (headcount >= 20)  return { label: "Large package", pct: 3  };
  if (headcount >= 10)  return { label: "Mid package",   pct: 5  };
  if (headcount >= 4)   return { label: "Crew",          pct: 8  };
  if (headcount >= 2)   return { label: "Small team",    pct: 12 };
  return                       { label: "Solo",          pct: 14 };
}

import type { FeeStatus } from "@/lib/api/types";
export function feeStatusTone(s: FeeStatus): "amber" | "blue" | "green" | "neutral" {
  if (s === "pending_invoice") return "amber";
  if (s === "invoiced")        return "blue";
  if (s === "paid")            return "green";
  if (s === "waived")          return "neutral";
  return "neutral";
}
export function feeStatusLabel(s: FeeStatus): string {
  if (s === "pending_invoice") return "Pending invoice";
  if (s === "invoiced")        return "Invoiced";
  if (s === "paid")            return "Paid";
  if (s === "waived")          return "Waived";
  return s;
}

export const COMMON_UK_LOCATIONS = [
  "Slough", "London", "Reading", "Maidenhead", "Crawley",
  "Manchester", "Liverpool", "Birmingham", "Coventry", "Leeds",
  "Sheffield", "Bristol", "Cardiff", "Edinburgh", "Glasgow",
  "Belfast", "Dublin",
];

export function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export function stageTone(stage: DealStage): "neutral" | "blue" | "green" | "amber" | "red" {
  if (stage === "completed") return "green";
  if (stage === "active") return "green";
  if (stage === "invited" || stage === "contracted" || stage === "introduced" || stage === "pending_onsite") return "blue";
  if (stage === "shortlisted" || stage === "negotiating" || stage === "requested") return "amber";
  if (stage === "cancelled") return "red";
  return "neutral";
}

export function stageLabel(stage: DealStage): string {
  const map: Record<DealStage, string> = {
    requested:      "Requested",
    invited:        "Invited",
    shortlisted:    "Shortlisted",
    negotiating:    "Negotiating",
    contracted:     "Contracted",
    introduced:     "Introduced",
    pending_onsite: "Pending onsite",
    active:         "Active",
    completed:      "Completed",
    cancelled:      "Cancelled",
  };
  return map[stage] || stage;
}
